<template>
  <div class="compact-analysis" v-if="show">
    <div class="analysis-header">
      <div class="header-left">
        <i class="fas fa-robot"></i>
        <span>Analyse IA</span>
      </div>
      <div class="header-right">
        <button @click="toggleAnalysis" class="collapse-btn" aria-label="Réduire/Agrandir l'analyse">
          <i :class="['fas', analysisExpanded ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
        </button>
      </div>
    </div>
    
    <transition name="slide-fade">
      <div v-if="analysisExpanded" class="analysis-content">      <!-- JSON Analysis summary section -->
   
     <!-- Summary section (no longer separately collapsible) -->
     <div class="summary-section">
      <!-- <div class="summary-header">
        <h4><i class="fas fa-chart-line"></i> Résumé de l'analyse</h4>
      </div> -->
        
          <div  class="summary-content">
            <!-- Parse and display JSON analysis -->
            <div v-if="analysisData" class="analysis-summary">
              <!-- Header stats with enhanced design -->
              <div class="analysis-stats">
                <div class="stat-card primary">
                  <div class="stat-icon"><i class="fas fa-users"></i></div>
                  <div class="stat-content">
                    <div class="stat-number">{{ analysisData.candidats_analyses }}</div>
                    <div class="stat-label">Candidats analysés</div>
                  </div>
                </div>
                <div class="stat-card success">
                  <div class="stat-icon"><i class="fas fa-user-check"></i></div>
                  <div class="stat-content">
                    <div class="stat-number">{{ getQualifiedCount() }}</div>
                    <div class="stat-label">Qualifiés</div>
                  </div>
                </div>
                <div class="stat-card danger">
                  <div class="stat-icon"><i class="fas fa-user-times"></i></div>
                  <div class="stat-content">
                    <div class="stat-number">{{ getEliminatedCount() }}</div>
                    <div class="stat-label">Éliminés</div>
                  </div>
                </div>
              </div>
      
              <!-- Position and criteria with improved layout -->
              <div class="analysis-meta">
                <div class="meta-header">
                  <h5><i class="fas fa-briefcase"></i> Détails du poste</h5>
                </div>
                <div class="meta-grid">
                  <div class="meta-item">
                    <span class="meta-label">Poste :</span>
                    <span class="meta-value">{{ analysisData.poste }}</span>
                  </div>
                  <div class="meta-item" v-if="analysisData.job_description">
                    <span class="meta-label">Exigences :</span>
                    <span class="meta-value">{{ analysisData.job_description }}</span>
                  </div>
                  <div class="meta-item">
                    <span class="meta-label">Critère critique :</span>
                    <span class="meta-value critical">{{ analysisData.exigence_critique }}</span>
                  </div>
                </div>
                <div class="result-banner">
                  <i class="fas fa-trophy"></i>
                  <span>{{ analysisData.resultat }}</span>
                </div>
              </div>
      
              <!-- Enhanced recommended candidate -->
              <div v-if="analysisData.candidat_recommande" class="recommended-candidate">
                <div class="section-header">
                  <h5><i class="fas fa-star"></i> Candidat recommandé</h5>
                </div>
                <div class="candidate-card recommended">
                  <div class="candidate-header">
                    <div class="candidate-info">
                      <span class="candidate-name">{{ analysisData.candidat_recommande.nom }}</span>
                      <div class="candidate-score-badge">
                        <i class="fas fa-medal"></i>
                        <span>{{ analysisData.candidat_recommande.score }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="candidate-proof">
                    <i class="fas fa-quote-left"></i>
                    <span>{{ analysisData.candidat_recommande.preuve }}</span>
                  </div>
                </div>
              </div>
      
              <!-- Enhanced other qualified candidates -->
              <div v-if="analysisData.autres_candidats_qualifies && analysisData.autres_candidats_qualifies.length > 0" class="other-qualified">
                <div class="section-header">
                  <h5><i class="fas fa-users"></i> Autres candidats qualifiés</h5>
                </div>
                <div class="qualified-list">
                  <div v-for="candidate in analysisData.autres_candidats_qualifies" :key="candidate.nom" class="candidate-card qualified">
                    <div class="candidate-header">
                      <div class="candidate-info">
                        <span class="candidate-name">{{ candidate.nom }}</span>
                        <div class="candidate-score-badge secondary">
                          <span>{{ candidate.score }}</span>
                        </div>
                      </div>
                    </div>
                    <div class="candidate-proof">
                      <i class="fas fa-quote-left"></i>
                      <span>{{ candidate.preuve }}</span>
                    </div>
                  </div>
                </div>
              </div>
      
              <!-- Enhanced eliminated candidates -->
              <div v-if="analysisData.candidats_elimines && analysisData.candidats_elimines.length > 0" class="eliminated-candidates">
                <div class="eliminated-header" @click="toggleEliminated">
                  <h5>
                    <i class="fas fa-times-circle"></i> 
                    Candidats éliminés ({{ analysisData.candidats_elimines.length }})
                    <i :class="['fas', showEliminated ? 'fa-chevron-up' : 'fa-chevron-down']" class="toggle-icon"></i>
                  </h5>
                </div>
                <transition name="slide-fade">
                  <div v-if="showEliminated" class="eliminated-list">
                    <div v-for="candidate in analysisData.candidats_elimines" :key="candidate.nom" class="eliminated-item">
                      <div class="eliminated-name">
                        <i class="fas fa-user"></i>
                        {{ candidate.nom }}
                      </div>
                      <div class="elimination-reason">{{ candidate.raison }}</div>
                    </div>
                  </div>
                </transition>
              </div>
      
              <!-- Enhanced conclusion -->
              <div v-if="analysisData.conclusion" class="conclusion">
                <div class="section-header">
                  <h5><i class="fas fa-lightbulb"></i> Conclusion</h5>
                </div>
                <div class="conclusion-content">
                  {{ analysisData.conclusion }}
                </div>
              </div>
      
              <!-- NEW: Interview Questions Section (displayed upfront) -->
              <div v-if="hasInterviewQuestionsInAnalysis()" class="interview-questions-section">
                <div class="section-header">
                  <h5><i class="fas fa-question-circle"></i> Questions d'entretien suggérées</h5>
                </div>
                <div class="questions-container">
                  <div v-if="analysisData.candidat_recommande && analysisData.candidat_recommande.interview_questions" class="candidate-questions">
                    <div class="questions-header">
                      <h6><i class="fas fa-star"></i> Pour {{ analysisData.candidat_recommande.nom }}</h6>
                    </div>
                    <ol class="question-list">
                      <li v-for="(question, index) in analysisData.candidat_recommande.interview_questions" :key="index" class="question-item">
                        {{ question }}
                      </li>
                    </ol>
                  </div>
                  
                  <div v-for="candidate in analysisData.autres_candidats_qualifies" :key="candidate.nom" 
                       v-if="candidate.interview_questions && candidate.interview_questions.length > 0" 
                       class="candidate-questions">
                    <div class="questions-header">
                      <h6><i class="fas fa-user"></i> Pour {{ candidate.nom }}</h6>
                    </div>
                    <ol class="question-list">
                      <li v-for="(question, index) in candidate.interview_questions" :key="index" class="question-item">
                        {{ question }}
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
      
            <!-- Fallback for non-JSON analysis -->
            <div v-else-if="analysis" class="formatted-content">
              {{ analysis }}
            </div>
            
            <div v-else class="no-content-message">
              <i class="fas fa-exclamation-triangle"></i>
              Analyse non disponible.
            </div>
          </div>
      </div>







      



      <!-- Comparison table section -->
      <div class="comparison-section" v-if="hasComparisonData">
        <h4>Tableau comparatif des candidats</h4>
        <div class="comparison-table-wrapper">
          <table class="comparison-table">
            <thead>
              <tr>
                <th class="candidate-col">Candidat</th>
                <th class="score-col">
                  <div class="tooltip-container">
                    Score global
                    <div class="tooltip-text">Score combiné de tous les facteurs d'évaluation</div>
                  </div>
                </th>
                <th>Recommandation</th>
                <th>Titre Déduit</th>
                <th>Forces</th>
                <th>Limites</th>
              </tr>
            </thead>
            <tbody>
              <tr 
                v-for="candidate in sortedCandidates" 
                :key="candidate.candidate_id || candidate.elasticsearch_id"
                :class="{'candidate-row': true, 'highlighted-row': candidate.rank === 1}"
                @click="selectCandidate(candidate)"
                style="cursor: pointer;"
              >
                <td class="candidate-name">
                  {{ candidate.candidate_name }}
                  <span class="rank-badge">{{ candidate.rank }}</span>
                </td>
                <td>
                  <div class="score-bar">
                    <div class="score-fill" :style="{width: `${candidate.overall_match_score || 0}%`, backgroundColor: getScoreColor(candidate.overall_match_score || 0)}"></div>
                    <span class="score-text">{{ candidate.overall_match_score || 0 }}%</span>
                  </div>
                </td>
                <td class="recommendation-cell">
                  <span :class="getRecommendationClass(candidate.hiring_recommendation)">
                    {{ formatRecommendation(candidate.hiring_recommendation) }}
                  </span>
                </td>
                <td>{{ candidate.inferred_title || 'N/A' }}</td>
                <td>{{ candidate.key_strengths || 'N/A' }}</td>
                <td>{{ candidate.limitations || 'N/A' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Enhanced technical breakdown section -->
      <div class="technical-breakdown-section" v-if="hasComparisonData && selectedCandidate">
        <h4>Analyse technique détaillée: {{ selectedCandidate.candidate_name }}</h4>
        
        <!-- Candidate selector dropdown -->
        <div class="candidate-selector" v-if="recommendedCandidates.length > 1">
          <label for="candidate-select">Candidat:</label>
          <select 
            id="candidate-select" 
            v-model="selectedCandidateId" 
            class="candidate-dropdown"
          >
            <option 
              v-for="candidate in sortedCandidates" 
              :key="candidate.candidate_id || candidate.elasticsearch_id"
              :value="candidate.candidate_id || candidate.elasticsearch_id"
            >
              {{ candidate.candidate_name }} ({{ candidate.overall_match_score || 0 }}%)
            </option>
          </select>
        </div>

        <!-- Technical skills breakdown -->
        <div class="skills-breakdown" v-if="selectedCandidate.technical_skill_breakdown">
          <h5>Répartition des compétences techniques</h5>
          <div class="skills-list">
            <div 
              v-for="(score, skill) in selectedCandidate.technical_skill_breakdown" 
              :key="skill" 
              class="skill-item"
            >
              <div class="skill-header">
                <span class="skill-name">{{ skill }}</span>
                <span class="skill-score">{{ score }}%</span>
              </div>
              <div class="skill-bar">
                <div 
                  class="skill-fill" 
                  :style="{
                    width: `${score}%`, 
                    backgroundColor: getSkillColor(score)
                  }"
                ></div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="no-skills-message">
          Aucune répartition technique disponible pour ce candidat.
        </div>

        <!-- Missing skills -->
        <div class="missing-skills" v-if="selectedCandidate.missing_critical_skills && selectedCandidate.missing_critical_skills.length > 0">
          <h5>Compétences manquantes</h5>
          <div class="missing-skills-list">
            <span 
              v-for="(skill, index) in selectedCandidate.missing_critical_skills" 
              :key="index" 
              class="missing-skill-tag"
            >
              {{ skill }}
            </span>
          </div>
        </div>
        
        <!-- Interview questions -->
        <div class="interview-questions" v-if="hasInterviewQuestions">
          <h5>Questions d'entretien suggérées</h5>
          <ul class="question-list">
            <li v-for="(question, index) in selectedCandidate.interview_questions" :key="index">
              {{ question }}
            </li>
          </ul>
        </div>
      </div>

    </div>
       </transition>


    <!---ddd-->
  </div>
</template>

<script>
export default {
  name: 'CompactAnalysis',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    analysis: {
      type: [String, Array],  // Accept both String and Array
      default: ''
    },
    recommendedCandidates: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      selectedCandidateId: null,
      showEliminated: false,
      analysisExpanded: true, // ← You have "summaryExpanded"


    }
  },
  computed: {
    // NEW: Parse JSON analysis data
    analysisData() {
  if (!this.analysis) return null;
  
  console.log('Raw analysis:', this.analysis);
  console.log('Analysis type:', typeof this.analysis);
  
  // Handle case where analysis is already an Array (which it is!)
  if (Array.isArray(this.analysis)) {
    console.log('Analysis is already an array');
    if (this.analysis.length > 0) {
      console.log('Returning first array element:', this.analysis[0]);
      return this.analysis[0];
    }
    return null;
  }
  
  // Handle case where analysis is an object
  if (typeof this.analysis === 'object' && this.analysis !== null) {
    console.log('Analysis is an object, returning directly:', this.analysis);
    return this.analysis;
  }
  
  // Handle case where analysis is a string (JSON)
  if (typeof this.analysis === 'string') {
    try {
      const cleanedAnalysis = this.analysis.trim();
      console.log('Cleaned analysis string:', cleanedAnalysis);
      
      const parsed = JSON.parse(cleanedAnalysis);
      console.log('Parsed JSON:', parsed);
      
      if (Array.isArray(parsed) && parsed.length > 0) {
        console.log('Returning first element from parsed array:', parsed[0]);
        return parsed[0];
      } else if (typeof parsed === 'object' && parsed !== null) {
        console.log('Returning parsed object:', parsed);
        return parsed;
      }
    } catch (e) {
      console.error('JSON parsing failed:', e);
      return null;
    }
  }
  
  console.log('No valid data structure found');
  return null;
},

    hasComparisonData() {
      return this.recommendedCandidates && 
             this.recommendedCandidates.length > 0;
    },
    
    sortedCandidates() {
      if (!this.recommendedCandidates || this.recommendedCandidates.length === 0) {
        return [];
      }
      
      // Sort by overall_match_score in descending order
      return [...this.recommendedCandidates].sort((a, b) => {
        const scoreA = a.overall_match_score || 0;
        const scoreB = b.overall_match_score || 0;
        return scoreB - scoreA;
      });
    },
    
    selectedCandidate() {
      if (!this.sortedCandidates || this.sortedCandidates.length === 0) {
        return null;
      }
      
      // If no candidate is explicitly selected, default to first (highest score)
      if (!this.selectedCandidateId) {
        return this.sortedCandidates[0];
      }
      
      // Find the selected candidate by ID
      const selected = this.sortedCandidates.find(c => {
        const candidateId = c.candidate_id || c.elasticsearch_id;
        return String(candidateId) === String(this.selectedCandidateId);
      });
      
      // Fallback to first candidate if selected one not found
      return selected || this.sortedCandidates[0];
    },
    
    hasInterviewQuestions() {
      return this.selectedCandidate && 
             this.selectedCandidate.interview_questions && 
             this.selectedCandidate.interview_questions.length > 0;
    }
  },
  
  watch: {
    recommendedCandidates: {
      handler(newCandidates) {
        // Auto-select the first (best) candidate when data changes
        if (newCandidates && newCandidates.length > 0) {
          const firstCandidate = this.sortedCandidates[0];
          if (firstCandidate) {
            this.selectedCandidateId = firstCandidate.candidate_id || firstCandidate.elasticsearch_id;
          }
        }
      },
      immediate: true
    }
  },
  
  methods: {
    toggleAnalysis() {
    this.analysisExpanded = !this.analysisExpanded;
  },
  
  hasInterviewQuestionsInAnalysis() {
    if (!this.analysisData) return false;
    
    // Check if recommended candidate has interview questions
    if (this.analysisData.candidat_recommande && 
        this.analysisData.candidat_recommande.interview_questions && 
        this.analysisData.candidat_recommande.interview_questions.length > 0) {
      return true;
    }
    
    // Check if any other qualified candidate has interview questions
    if (this.analysisData.autres_candidats_qualifies) {
      return this.analysisData.autres_candidats_qualifies.some(candidate => 
        candidate.interview_questions && candidate.interview_questions.length > 0
      );
    }
    
    return false;
  },
    // NEW: Helper methods for JSON analysis
    getQualifiedCount() {
      if (!this.analysisData) return 0;
      let count = 0;
      if (this.analysisData.candidat_recommande) count++;
      if (this.analysisData.autres_candidats_qualifies) count += this.analysisData.autres_candidats_qualifies.length;
      return count;
    },

    getEliminatedCount() {
      return this.analysisData?.candidats_elimines?.length || 0;
    },

    toggleEliminated() {
      this.showEliminated = !this.showEliminated;
    },

    selectCandidate(candidate) {
      this.selectedCandidateId = candidate.candidate_id || candidate.elasticsearch_id;
    },
    
    getScoreColor(score) {
      if (score >= 80) return '#27ae60';
      if (score >= 60) return '#f39c12';
      if (score >= 40) return '#e67e22';
      return '#e74c3c';
    },
    
    getSkillColor(score) {
      if (score >= 80) return '#2ecc71';
      if (score >= 60) return '#f1c40f';
      if (score >= 40) return '#e67e22';
      return '#e74c3c';
    },
    
    formatRecommendation(recommendation) {
      const map = {
        'vivement_recommandé': 'Vivement recommandé',
        'vivement recommandé': 'Vivement recommandé',
        'recommandé': 'Recommandé',
        'peut-être': 'Peut-être',
        'non_recommandé': 'Non recommandé',
        'non recommandé': 'Non recommandé'
      };
      
      const key = recommendation?.toLowerCase();
      return map[key] || recommendation;
    },
    
    getRecommendationClass(recommendation) {
      if (!recommendation) return '';
      
      const rec = recommendation.toLowerCase();
      
      if (rec.includes('vivement') || rec.includes('strong') || rec.includes('fort')) {
        return 'recommendation-strong';
      }
      if (rec.includes('recommandé') || rec.includes('yes') || rec.includes('oui')) {
        return 'recommendation-yes';
      }
      if (rec.includes('peut') || rec.includes('maybe') || rec.includes('possible')) {
        return 'recommendation-maybe';
      }
      if (rec.includes('non') || rec.includes('no')) {
        return 'recommendation-no';
      }
      
      return '';
    }
  }
};
</script>

<style scoped>
/* Base component styling */
.compact-analysis {
  border: 1px solid rgba(234, 94, 19, 0.3);
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

.analysis-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  background:linear-gradient(135deg, rgba(212,47,61,0.3), rgba(212,47,61,0.1));
  border-bottom: 1px solid rgba(234, 94, 19, 0.3);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-weight: 600;
  font-size: 1.1rem;
}

.header-left i {
  color: #D42F3D;
  font-size: 1.2rem;
}
.collapse-btn {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 4px;
  transition: all 0.2s ease;
  font-size: 1.1rem;
}

.collapse-btn:hover {
  color: white;
  background: rgba(234, 94, 19, 0.2);
}
.analysis-content {
  padding: 1.5rem;
  overflow-y: auto;
  max-height: 70vh;
}


.stat-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(234, 94, 19, 0.2);
  border-radius: 8px;
  padding: 1rem;
  text-align: center;
  transition: all 0.2s ease;
}

.stat-card:hover {
  border-color: rgba(234, 94, 19, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

.stat-number {
  font-size: 2rem;
  font-weight: 700;
  color: #D42F3D;
  line-height: 1;
}

.stat-label {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-top: 0.25rem;
}

.analysis-meta {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(234, 94, 19, 0.15);
  border-radius: 8px;
  padding: 1.25rem;
}

.meta-item {
  margin-bottom: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

.meta-item:last-child {
  margin-bottom: 0;
}

.result-highlight {
  color: #D42F3D;
  font-weight: 600;
  font-size: 1.05rem;
}

.recommended-candidate,
.other-qualified {
  margin-top: 1rem;
}

.candidate-card {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(234, 94, 19, 0.2);
  border-radius: 8px;
  padding: 1rem;
  margin-bottom: 0.75rem;
  transition: all 0.2s ease;
}

.candidate-card:hover {
  border-color: rgba(234, 94, 19, 0.4);
  background: rgba(255, 255, 255, 0.08);
}

.candidate-card.recommended {
  border-color: rgba(39, 174, 96, 0.4);
  background: rgba(39, 174, 96, 0.08);
}

.candidate-card.qualified {
  border-color: rgba(52, 152, 219, 0.4);
  background: rgba(52, 152, 219, 0.08);
}

.candidate-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.candidate-name {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  font-size: 1.05rem;
}

.score-badge {
  background: #D42F3D;
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.85rem;
}

.score-badge.secondary {
  background: rgba(52, 152, 219, 0.8);
}

.candidate-proof {
  color: rgba(255, 255, 255, 0.8);
  line-height: 1.5;
  font-style: italic;
}

.eliminated-candidates {
  margin-top: 1rem;
}

.eliminated-header {
  cursor: pointer;
  transition: color 0.2s ease;
}

.eliminated-header:hover h5 {
  color: #D42F3D;
}

.toggle-icon {
  margin-left: 0.5rem;
  font-size: 0.8rem;
  transition: transform 0.2s ease;
}

.eliminated-list {
  margin-top: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.eliminated-item {
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.3);
  border-radius: 6px;
  padding: 1rem;
}

.eliminated-name {
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0.5rem;
}

.elimination-reason {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.9rem;
  line-height: 1.4;
}

.conclusion {
  margin-top: 1rem;
}

.conclusion-text {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(234, 94, 19, 0.15);
  border-radius: 8px;
  padding: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-style: italic;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Section headers */
h4, h5 {
  color: #D42F3D;
  margin-top: 0;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

h4 {
  font-size: 1.15rem;
  font-weight: 600;
}

h5 {
  font-size: 1rem;
  font-weight: 600;
  margin-top: 1rem;
}

h5 i {
  color: #D42F3D;
  font-size: 0.9rem;
}


.formatted-content {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(234, 94, 19, 0.15);
  border-radius: 8px;
  padding: 1.25rem;
}

.no-content-message, .no-skills-message {
  color: rgba(255, 255, 255, 0.6);
  font-style: italic;
  text-align: center;
  padding: 2rem;
}

/* Comparison table styling */
.comparison-table-wrapper {
  overflow-x: auto;
  border-radius: 8px;
  border: 1px solid rgba(234, 94, 19, 0.2);
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.comparison-table th,
.comparison-table td {
  padding: 0.75rem;
  text-align: left;
  border-bottom: 1px solid rgba(234, 94, 19, 0.15);
}

.comparison-table th {
  background: rgba(234, 94, 19, 0.15);
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.85rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.candidate-row:hover {
  background: rgba(234, 94, 19, 0.1);
}

.highlighted-row {
  background: rgba(39, 174, 96, 0.1);
}

.candidate-name {
  font-weight: 500;
  position: relative;
}

.rank-badge {
  background: #D42F3D;
  color: white;
  font-size: 0.7rem;
  padding: 0.2rem 0.4rem;
  border-radius: 3px;
  margin-left: 0.5rem;
}

.score-bar {
  position: relative;
  width: 100px;
  height: 20px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  overflow: hidden;
}

.score-fill {
  height: 100%;
  border-radius: 10px;
  transition: width 0.3s ease;
}

.score-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 0.75rem;
  font-weight: 600;
  color: white;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.7);
}

.recommendation-strong { color: #2ecc71; font-weight: 600; }
.recommendation-yes { color: #3498db; }
.recommendation-maybe { color: #f39c12; }
.recommendation-no { color: #e74c3c; }

/* Candidate selector */
.candidate-selector {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.candidate-dropdown {
  background: rgba(40, 40, 50, 0.8);
  border: 1px solid rgba(234, 94, 19, 0.3);
  color: white;
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.9rem;
  min-width: 250px;
}

.candidate-dropdown:focus {
  outline: none;
  border-color: #D42F3D;
}

/* Technical skills breakdown */
.skills-breakdown {
  margin-top: 1rem;
}

.skills-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.skill-item {
  background: rgba(40, 40, 50, 0.4);
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(234, 94, 19, 0.2);
}

.skill-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.skill-name {
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.skill-bar {
  position: relative;
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.skill-fill {
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.skill-score {
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 600;
}

/* Missing skills */
.missing-skills {
  margin-top: 1rem;
}

.missing-skills-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.missing-skill-tag {
  background: rgba(244, 67, 54, 0.2);
  color: rgba(255, 255, 255, 0.9);
  padding: 0.4rem 0.8rem;
  border-radius: 16px;
  font-size: 0.8rem;
  border: 1px solid rgba(244, 67, 54, 0.4);
}

/* Interview questions */
.interview-questions {
  margin-top: 1rem;
}

.question-list {
  margin: 0;
  padding-left: 1.5rem;
}

.question-list li {
  margin-bottom: 0.75rem;
  font-size: 0.9rem;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
}

/* Tooltip styling */
.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip-text {
  visibility: hidden;
  width: 200px;
  background-color: rgba(40, 40, 50, 0.95);
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  margin-left: -100px;
  opacity: 0;
  transition: opacity 0.3s;
  font-weight: normal;
  font-size: 0.75rem;
  pointer-events: none;
  border: 1px solid rgba(234, 94, 19, 0.3);
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

/* Scrollbar styling */
.analysis-content::-webkit-scrollbar {
  width: 8px;
}

.analysis-content::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.2);
}

.analysis-content::-webkit-scrollbar-thumb {
  background: rgba(234, 94, 19, 0.4);
  border-radius: 4px;
}

.analysis-content::-webkit-scrollbar-thumb:hover {
  background: rgba(234, 94, 19, 0.6);
}

/* Responsive design */
@media (max-width: 768px) {
  .analysis-stats {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
  }
  
  .candidate-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  
  .analysis-content {
    padding: 1rem;
  }
}
/* Summary section header */
.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.25rem;
  background:linear-gradient(135deg, rgba(212,47,61,0.3), rgba(212,47,61,0.1));
  border-bottom: 1px solid rgba(234, 94, 19, 0.3);
  border-radius: 8px 8px 0 0;
}

.summary-header h4 {
  margin: 0;
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.summary-header h4 i {
  color: #D42F3D;
  font-size: 1.2rem;
}

.summary-content {
  padding: 1.5rem;
}

/* Enhanced analysis stats */
.analysis-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(234, 94, 19, 0.2);
  border-radius: 8px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  transition: all 0.2s ease;
  backdrop-filter: blur(5px);
}

.stat-card:hover {
  border-color: rgba(234, 94, 19, 0.4);
  background: rgba(255, 255, 255, 0.06);
  transform: translateY(-2px);
}

.stat-card.primary { border-color: rgba(234, 94, 19, 0.4); }
.stat-card.success { border-color: rgba(39, 174, 96, 0.4); }
.stat-card.danger { border-color: rgba(231, 76, 60, 0.4); }

.stat-icon {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
}

.stat-card.primary .stat-icon {
  background: rgba(234, 94, 19, 0.2);
  color: #D42F3D;
}

.stat-card.success .stat-icon {
  background: rgba(39, 174, 96, 0.2);
  color: #27ae60;
}

.stat-card.danger .stat-icon {
  background: rgba(231, 76, 60, 0.2);
  color: #e74c3c;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 1.75rem;
  font-weight: 700;
  color: white;
  line-height: 1;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1;
}

/* Enhanced analysis meta */
.analysis-meta {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(234, 94, 19, 0.2);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 2rem;
}

.meta-header {
  padding: 1rem 1.25rem;
  background: rgba(234, 94, 19, 0.1);
  border-bottom: 1px solid rgba(234, 94, 19, 0.2);
}

.meta-header h5 {
  margin: 0;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.meta-header i {
  color: #D42F3D;
}

.meta-grid {
  padding: 1.25rem;
}

.meta-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  margin-bottom: 1rem;
}

.meta-item:last-child {
  margin-bottom: 0;
}

.meta-label {
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.7);
  font-weight: 500;
}

.meta-value {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.95rem;
  line-height: 1.4;
}

.meta-value.critical {
  color: #D42F3D;
  font-weight: 600;
}

.result-banner {
  background:linear-gradient(135deg, rgba(212,47,61,0.3), rgba(212,47,61,0.1));
  border: 1px solid rgba(234, 94, 19, 0.3);
  padding: 1rem 1.25rem;
  margin: 1.25rem;
  margin-bottom: 0;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: white;
  font-weight: 600;
  font-size: 1.05rem;
}

.result-banner i {
  color: #D42F3D;
  font-size: 1.2rem;
}

/* Section headers */
.section-header {
  margin-bottom: 1rem;
}

.section-header h5 {
  margin: 0;
  color: white;
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid rgba(234, 94, 19, 0.3);
}

.section-header i {
  color: #D42F3D;
}

/* Enhanced candidate cards */
.candidate-card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(234, 94, 19, 0.2);
  border-radius: 8px;
  padding: 1.25rem;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
  backdrop-filter: blur(5px);
}

.candidate-card:hover {
  border-color: rgba(234, 94, 19, 0.4);
  background: rgba(255, 255, 255, 0.06);
  transform: translateY(-1px);
}

.candidate-card.recommended {
  border-color: rgba(39, 174, 96, 0.4);
  background: rgba(39, 174, 96, 0.05);
}

.candidate-card.qualified {
  border-color: rgba(52, 152, 219, 0.4);
  background: rgba(52, 152, 219, 0.05);
}

.candidate-header {
  margin-bottom: 1rem;
}

.candidate-info {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.candidate-name {
  font-weight: 600;
  font-size: 1.1rem;
  color: white;
}

.candidate-score-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: rgba(234, 94, 19, 0.2);
  padding: 0.5rem 0.75rem;
  border-radius: 16px;
  font-weight: 600;
  color: white;
  font-size: 0.9rem;
}

.candidate-score-badge.secondary {
  background: rgba(52, 152, 219, 0.2);
}

.candidate-score-badge i {
  color: #D42F3D;
}

.candidate-proof {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(234, 94, 19, 0.15);
  border-radius: 6px;
  padding: 1rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
  font-style: italic;
  display: flex;
  gap: 0.75rem;
}

.candidate-proof i {
  color: rgba(234, 94, 19, 0.6);
  font-size: 0.875rem;
  margin-top: 0.125rem;
  flex-shrink: 0;
}

/* Enhanced eliminated candidates */
.eliminated-header {
  cursor: pointer;
  padding: 1rem;
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 8px;
  margin-bottom: 1rem;
  transition: all 0.2s ease;
}

.eliminated-header:hover {
  background: rgba(231, 76, 60, 0.15);
  border-color: rgba(231, 76, 60, 0.3);
}

.eliminated-header h5 {
  margin: 0;
  color: white;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  justify-content: space-between;
}

.eliminated-header i.fas.fa-times-circle {
  color: #e74c3c;
}

.toggle-icon {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.875rem;
  transition: transform 0.2s ease;
}

.eliminated-item {
  background: rgba(231, 76, 60, 0.05);
  border: 1px solid rgba(231, 76, 60, 0.2);
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 0.75rem;
}

.eliminated-name {
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.eliminated-name i {
  color: #e74c3c;
}

.elimination-reason {
  color: rgba(255, 255, 255, 0.8);
  font-size: 0.9rem;
  line-height: 1.4;
}

/* Enhanced conclusion */
.conclusion {
  margin-top: 2rem;
}

.conclusion-content {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(234, 94, 19, 0.2);
  border-radius: 8px;
  padding: 1.25rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  font-size: 0.95rem;
}

/* NEW: Interview Questions Section */
.interview-questions-section {
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid rgba(234, 94, 19, 0.3);
}

.questions-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.candidate-questions {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(234, 94, 19, 0.2);
  border-radius: 8px;
  overflow: hidden;
}

.questions-header {
  padding: 1rem 1.25rem;
  background: rgba(234, 94, 19, 0.1);
  border-bottom: 1px solid rgba(234, 94, 19, 0.2);
}

.questions-header h6 {
  margin: 0;
  color: white;
  font-weight: 600;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.questions-header i {
  color: #D42F3D;
}

.question-list {
  padding: 1.25rem;
  margin: 0;
  counter-reset: question-counter;
}

.question-item {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin-bottom: 1rem;
  padding-left: 0.5rem;
  counter-increment: question-counter;
  font-size: 0.95rem;
}

.question-item:last-child {
  margin-bottom: 0;
}

.question-item::marker {
  color: #D42F3D;
  font-weight: 600;
}

/* Transitions */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-1rem);
}

/* Responsive design */
@media (max-width: 768px) {
  .analysis-stats {
    grid-template-columns: 1fr;
  }
  
  .candidate-info {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
  
  .summary-content {
    padding: 1rem;
  }
  
  .meta-grid {
    padding: 1rem;
  }
}
</style>